import { z } from "zod";
import { NativeInvoiceTermType } from "../../../helpers/invoice-terms.helper";
import {
    InvoiceSentStatus,
    InvoiceSource,
    InvoiceStatus,
} from "../../../types/invoice";
import { DocumentMatchStatus } from "../../../types/matchStatus";
import { CatalogItemFilterType } from "../../../types/catalogItem";
import { zQueryUuidArray } from "../../query-uuid-array.schema";
import { PG_INT4_MAX } from "../../shared.schema";
import { InvoiceStatusFilterSchema } from "./invoice-status-filter.schema";

const InvoiceStatusArraySchema = z.preprocess(
    (value) => (typeof value === "string" ? [value] : value),
    z.array(z.nativeEnum(InvoiceStatus)),
);

export const InvoiceFiltersSchema = z.object({
    ids: z.array(z.string()).optional(),
    catalogItemIds: zQueryUuidArray().optional(),
    catalogItemFilterType: z.nativeEnum(CatalogItemFilterType).optional(),
    sourceTemplateIds: zQueryUuidArray().optional(),
    search: z.string().optional(),
    entityIds: z.array(z.coerce.number()).optional(),
    invoiceStatuses: InvoiceStatusArraySchema.optional(),
    externalInvoiceStatuses: InvoiceStatusArraySchema.optional(),
    invoiceStatusFilter: InvoiceStatusFilterSchema.optional(),
    start: z.coerce.date().optional(),
    end: z.coerce.date().optional(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    createdOnFrom: z.string().date().optional(),
    createdOnTo: z.string().date().optional(),
    invoiceDateFrom: z.string().date().optional(),
    invoiceDateTo: z.string().date().optional(),
    overdue: z.coerce.boolean().optional(),
    minOverdue: z.coerce.number().optional(),
    maxOverdue: z.coerce.number().optional(),
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
    accountIds: z.array(z.coerce.number()).optional(),
    // Selects invoices by their source rather than a specific financial
    // account. Currently only Kick (MANUAL, i.e. native invoices with no
    // connected financial account) is offered as a filterable value; it is
    // combined with `accountIds` via OR so both can be selected together in
    // the Account filter.
    invoiceSources: z.array(z.nativeEnum(InvoiceSource)).optional(),
    counterpartiesIds: z.array(z.string()).optional(),
    withEmptyOrUnverifiedCounterparty: z.boolean().optional(),
    isSubscription: z.coerce.boolean().optional(),
    hasOpenAmount: z.coerce.boolean().optional(),
    matchStatuses: z.array(z.nativeEnum(DocumentMatchStatus)).optional(),
    termTypes: z.array(z.nativeEnum(NativeInvoiceTermType)).optional(),
    sentStatuses: z.array(z.nativeEnum(InvoiceSentStatus)).optional(),
    sentByUserIds: z
        .array(z.coerce.number().int().min(1).max(PG_INT4_MAX))
        .optional(),
});

export type InvoiceFilters = z.infer<typeof InvoiceFiltersSchema>;
